import React from 'react';
import useAuth from '../hooks/useAuth';
import Button from '@mui/material/Button';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { green, red } from '@mui/material/colors';
import { toast, ToastContainer } from 'react-toastify';
import { CssBaseline, Grid, TextField, IconButton, InputAdornment, Box, Typography } from '@mui/material';
import RadioButton from '../common/RadioButton';
import { adminCreateUser, adminGetProfileByPhone, adminGetProfileByEmail } from '../../services/admin';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';

export const modalStyle = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: 400,
	bgcolor: 'background.paper',
	border: '2px solid #000',
	boxShadow: 24,
	p: 4,
};

export const theme = createTheme({
	palette: {
		green: {
			main: green[500],
		},
		red: {
			main: red[500],
		},
	},
});

export const AdminCreatePofile = () => {
	const { getToken } = useAuth();
	const [email, setEmail] = React.useState('');
	const [emailError, setEmailError] = React.useState(false);

	const [phone, setPhone] = React.useState('');
	const [phoneError, setPhoneError] = React.useState(false);

	const [firstName, setFirstName] = React.useState('');
	const [lastName, setLastName] = React.useState('');

	const [newPassword, setNewPassword] = React.useState('');
	const [newPasswordError, setNewPasswordError] = React.useState(false);

	const [choice, setChoice] = React.useState('start');

	const [existingUser, setExistingUser] = React.useState(null);
	const [errorMessage, setErrorMessage] = React.useState('');

	const isEmailValid = () => {
		//regex email validation
		const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
		if (emailRegex.test(email)) {
			setEmailError(false);
			return true;
		} else {
			setEmailError(true);
			return false;
		}
	};
	const isPhoneValid = () => {
		//Phone number validation
		const phoneRegex = /^[0-9]{10}$/;
		if (phoneRegex.test(phone)) {
			setPhoneError(false);
			return true;
		} else {
			setPhoneError(true);
			return false;
		}
	};

	const generateAutoPassword = () => {
		if (!firstName || !lastName) {
			toast.error('Please enter first name and last name first');
			return;
		}

		const fullName = (firstName + lastName).replace(/\s+/g, '');
		const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';
		const numbers = '0123456789';
		
		// Get 3 random letters from the name
		let letters = '';
		for (let i = 0; i < 3; i++) {
			const randomIndex = Math.floor(Math.random() * fullName.length);
			letters += fullName[randomIndex];
		}
		
		// Get 1 random special character
		const specialIndex = Math.floor(Math.random() * specialChars.length);
		const special = specialChars[specialIndex];
		
		// Get 2 random numbers
		let nums = '';
		for (let i = 0; i < 2; i++) {
			const randomIndex = Math.floor(Math.random() * numbers.length);
			nums += numbers[randomIndex];
		}
		
		// Combine and shuffle
		const password = (letters + special + nums).split('').sort(() => Math.random() - 0.5).join('');
		setNewPassword(password);
		setNewPasswordError(false);
	};

	const handleChange = (event) => {
		const { name, value } = event.target;
		switch (name) {
			case 'email':
				setEmail(value);
				setEmailError(false);
				setExistingUser(null);
				setErrorMessage('');
				break;
			case 'phone':
				setPhone(value);
				setPhoneError(false);
				setExistingUser(null);
				setErrorMessage('');
				break;
			case 'firstName':
				setFirstName(value);
				break;
			case 'lastName':
				setLastName(value);
				break;
			case 'choice':
				setChoice(value);
				break;
			case 'password':
				setNewPassword(value);
				setNewPasswordError(false);
				break;
			default:
				break;
		}
	};

	const handleCopyPassword = () => {
		navigator.clipboard.writeText(newPassword);
		toast.success('Password copied to clipboard!');
	};

	const handleRefresh = () => {
		window.location.reload();
	};

	const isPasswordValid = () => {
		if (newPassword.length === 0) {
			setNewPasswordError(true);
			return false;
		}
		return true;
	};

	const createNewProfile = async (
		registrationKey,
		password,
		registrationType
	) => {
		let auth = getToken();
		if (auth) {
			try {
				let response = await adminCreateUser(
					auth.token,
					registrationKey,
					password,
					registrationType === 'Phone'
				);
				if (response.data.errors) {
					const msg = response.data.errors[0]?.message?.toLowerCase() || '';
					setErrorMessage(response.data.errors[0]?.message || '');
					if (msg.includes('user already exists')) {
						// Fetch user by phone/email
						let userResp;
						if (registrationType === 'Phone') {
							userResp = await adminGetProfileByPhone(auth.token, "+91" + registrationKey);
							const userData = userResp?.data?.data?.adminGetProfileByPhone;
							const userId = userData?.profile?.id;
							const firstName = userData?.profile?.firstName;
							const lastName = userData?.profile?.lastName;
							if (userId) setExistingUser({ id: userId, firstName, lastName });
						} else {
							userResp = await adminGetProfileByEmail(auth.token, registrationKey);
							const userData = userResp?.data?.data?.adminGetProfileByEmail;
							const userId = userData?.profile?.id;
							const firstName = userData?.profile?.firstName;
							const lastName = userData?.profile?.lastName;
							if (userId) setExistingUser({ id: userId, firstName, lastName });
						}
					} else {
						setExistingUser(null);
						toast.error(
							'There was an error in Creating the profile. Please try after sometime.',
							{
								timeOut: 1000,
							}
						);
						console.error(
							'error in resetting password while using axios',
							response.data.errors
						);
					}
				} else {
					setChoice('end');
				}
			} catch (error) {
				console.error(
					'error in resetting password while using axios',
					error
				);
				toast.error(
					'There was an error in creating the Profile. Please try after sometime.',
					{
						timeOut: 1000,
					}
				);
			}
		}
	};

	const handleSubmit = async () => {
		if (choice === 'start') {
			toast.info('Please choose your account registration method', {
				timeOut: 1000,
			});
		} else {
			if (choice === 'Phone') {
				isPhoneValid() &&
					isPasswordValid() &&
					(await createNewProfile(phone, newPassword, choice));
			} else {
				isEmailValid() &&
					isPasswordValid() &&
					(await createNewProfile(email, newPassword, choice));
			}
		}
	};

	return (
		<>
			<ToastContainer />
			<div className='container-fluid mt-1 mb-1'>
				<div className='row home-row'>
					<h3>Create New User</h3>

					<ThemeProvider theme={theme}>
						<Grid container component='main'>
							<CssBaseline />
							<ToastContainer />

							<Grid item xs={6}>
								{choice !== 'end' ? (
									<>
										<RadioButton
											label='Choose account registration method'
											name='choice'
											value={choice}
											onChange={handleChange}
											options={[
												{
													value: 'Phone',
													label: 'Phone',
												},
												{
													value: 'Email',
													label: 'Email',
												},
											]}
										/>

										{/* First Name */}
										<TextField
											variant='standard'
											margin='normal'
											required
											fullWidth
											id='firstName'
											label='First Name'
											name='firstName'
											value={firstName}
											onChange={handleChange}
										/>

										{/* Last Name */}
										<TextField
											variant='standard'
											margin='normal'
											required
											fullWidth
											id='lastName'
											label='Last Name'
											name='lastName'
											value={lastName}
											onChange={handleChange}
										/>

										{/* PHONE NUMBER */}
										{choice === 'Phone' && (
											<TextField
												variant='standard'
												margin='normal'
												required
												fullWidth
												id='phone'
												maxLength='10'
												label='Phone Number'
												placeholder='Enter your phone number'
												name='phone'
												autoComplete='phone'
												error={phoneError}
												onChange={handleChange}
												helperText={
													phoneError
														? 'Phone number is invalid'
														: ''
												}
											/>
										)}
										{/* EMAIL */}
										{choice === 'Email' && (
											<TextField
												variant='standard'
												margin='normal'
												required
												fullWidth
												id='email'
												label='Email Address'
												name='email'
												autoComplete='email'
												placeholder='Enter your email'
												error={emailError}
												onChange={handleChange}
												helperText={
													emailError
														? 'Email is invalid'
														: ''
												}
											/>
										)}
										<TextField
											label={'Password'}
											name={'password'}
											fullWidth
											type='text'
											value={newPassword}
											placeholder={'Enter new Password'}
											onChange={handleChange}
											style={{ marginTop: 5 }}
											error={newPasswordError}
											helperText={
												newPasswordError
													? 'Password must have atleast one character'
													: ''
											}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															onClick={generateAutoPassword}
															edge="end"
															title="Auto Generate Password"
														>
															<AutoFixHighIcon />
														</IconButton>
													</InputAdornment>
												),
											}}
										/>
										<Button
											fullWidth
											variant='contained'
											sx={{ mt: 3, mb: 2 }}
											onClick={handleSubmit}
										>
											Create Profile
										</Button>
									</>
								) : (
									<>
										{/* END */}
										{choice === 'end' && (
											<Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
												<Typography variant="h6" gutterBottom>
													Profile Created Successfully
												</Typography>
												<Typography variant="body1" gutterBottom>
													User with{' '}
													{phone.length > 0
														? 'Phone ' + phone
														: 'Email ' + email}{' '}
													has been created.
												</Typography>
												<Typography variant="body1" gutterBottom>
													Generated Password:
												</Typography>
												<Box sx={{ 
													display: 'flex', 
													alignItems: 'center', 
													gap: 1,
													mb: 2,
													p: 1,
													bgcolor: '#f5f5f5',
													borderRadius: 1
												}}>
													<Typography variant="body1" sx={{ flex: 1 }}>
														{newPassword}
													</Typography>
													<IconButton onClick={handleCopyPassword} title="Copy Password">
														<ContentCopyIcon />
													</IconButton>
												</Box>
												<Button
													variant="contained"
													startIcon={<RefreshIcon />}
													onClick={handleRefresh}
													fullWidth
												>
													Create Another Profile
												</Button>
											</Box>
										)}
									</>
								)}
								{existingUser && (
									<div style={{ margin: '10px 0', color: '#b91c1c', fontSize: '0.98em' }}>
										User already exists. <a href={`/administratorpath/viewprofile/${existingUser.id}`} style={{ color: '#2563eb', textDecoration: 'underline' }}>View existing user profile{existingUser.firstName ? ` (${existingUser.firstName} ${existingUser.lastName || ''})` : ' (No Name)'}</a>
									</div>
								)}
								{errorMessage && !existingUser && (
									<div style={{ margin: '10px 0', color: '#b91c1c', fontSize: '0.98em' }}>{errorMessage}</div>
								)}
							</Grid>
						</Grid>
					</ThemeProvider>
				</div>
			</div>
		</>
	);
};
